import { useState, useCallback, useEffect } from 'react';
import { save, getLocalItem } from 'lib/LocalStorage';
import * as User from 'data/User';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { STORAGE_KEYS, GENERAL_CONSTANTS } from 'constants/general';

interface UseAdminViewToggleProps {
  user: User.default | null;
  vendorsList: Vendor[];
  businessUnitList: BusinessUnit[];
  onVendorChange: (vendor: Vendor | null) => void;
  onBusinessUnitChange: (businessUnit: BusinessUnit | null) => void;
}

interface UseAdminViewToggleReturn {
  isAdminView: boolean;
  canToggleAdminView: boolean;
  handleAdminViewToggle: (newAdminView: boolean) => void;
  handleVendorSelection: (vendorName: string, brandId?: number) => void;
  handleBusinessUnitSelection: (businessUnitName: string | null) => void;
}

/**
 * Custom hook for managing admin view toggle functionality
 * 
 * This hook encapsulates all the logic related to the admin view toggle feature:
 * - State management for admin view on/off
 * - Session storage persistence
 * - Automatic vendor/business unit selection fallbacks
 * - Auto-disable admin view when manual selections are made
 * 
 * @example
 * ```typescript
 * const {
 *   isAdminView,
 *   canToggleAdminView,
 *   handleAdminViewToggle,
 *   handleVendorSelection,
 *   handleBusinessUnitSelection
 * } = useAdminViewToggle({
 *   user,
 *   vendorsList,
 *   businessUnitList,
 *   onVendorChange: setSelectedVendor,
 *   onBusinessUnitChange: setSelectedBusinessUnit
 * });
 * ```
 * 
 * @requirements
 * - When admin view is turned off, fallback to first vendor/BU in list
 * - When vendor/BU is manually selected, auto-disable admin view
 * - Only OFMP admin users can toggle admin view
 */
export const useAdminViewToggle = ({
  user,
  vendorsList,
  businessUnitList,
  onVendorChange,
  onBusinessUnitChange
}: UseAdminViewToggleProps): UseAdminViewToggleReturn => {
  
  // Initialize admin view state from local storage or default to user role
  const getInitialAdminViewState = (): boolean => {
    const isOfmpAdmin = User.isOfmpAdmin(user!);
    if (!isOfmpAdmin) {
      // Clear any stored admin view data if user is not OFMP admin
      localStorage.removeItem(STORAGE_KEYS.ADMIN_VIEW);
      return false;
    }
    
    try {
      const storedAdminView = localStorage.getItem(STORAGE_KEYS.ADMIN_VIEW);
      return storedAdminView !== null ? JSON.parse(storedAdminView) : true; // Default to true for OFMP admins
    } catch {
      return true; // Default to true for OFMP admins
    }
  };

  const [isAdminView, setIsAdminView] = useState<boolean>(getInitialAdminViewState);

  // Persist admin view state to localStorage whenever it changes
  useEffect(() => {
    if (User.isOfmpAdmin(user!)) {
      localStorage.setItem(STORAGE_KEYS.ADMIN_VIEW, JSON.stringify(isAdminView));
    }
  }, [isAdminView, user]);

  // Determine if user can toggle admin view (only OFMP admins)
  const canToggleAdminView = user ? (User.isOfmpAdmin(user) || User.hasOfmpAdminProfile(user)) : false;

  /**
   * Handles admin view toggle on/off
   * When turning off, automatically selects first vendor/business unit
   */
  const handleAdminViewToggle = useCallback((newAdminView: boolean) => {
    if (!canToggleAdminView) {
      console.warn(GENERAL_CONSTANTS.UNAUTHORIZED_ADMIN_TOGGLE);
      return;
    }

    setIsAdminView(newAdminView);
    
    if (newAdminView) {
      // When switching to admin view, reset vendor and business unit selection
      onVendorChange(null);
      onBusinessUnitChange(null);
    } else if (vendorsList.length > 0) {
      // When switching back from admin view, fallback to first vendor with "All" business units
      // Since toggle is only visible to admins, we only handle admin case
      const targetVendor = vendorsList[0] || null;
      
      onVendorChange(targetVendor);
      onBusinessUnitChange(null); // Select "All" business units instead of specific one
    }
  }, [
    canToggleAdminView, 
    vendorsList, 
    businessUnitList, 
    onVendorChange, 
    onBusinessUnitChange
  ]);

  const disableAdminView = useCallback(() => {
    if (isAdminView && canToggleAdminView) {
      setIsAdminView(false);
    }
  }, [isAdminView, canToggleAdminView]);

  /**
   * Handles vendor selection with automatic admin view disable
   * Auto-disable admin view when vendor is manually selected
   */
  const handleVendorSelection = useCallback((vendorName: string, brandId?: number) => {
    // Deactivated units stay in businessUnitList so the organization screens can
    // show them, and the dropdown marks them disabled. This path selects a unit
    // programmatically, from a product's brand, so it has to make the same check
    // itself — otherwise opening a product whose unit was deactivated puts that
    // unit in the header, where it cannot be picked by hand.
    const foundBusinessUnit = businessUnitList.find(
      bu => bu.brandId === brandId && !bu.deletedOn
    );

    // If a business unit is found with the given brandId, set it as selected
    if (foundBusinessUnit) {
      const vendor = vendorsList.find(vendor => vendor.id === foundBusinessUnit?.vendorId) || null;
      
      onVendorChange(vendor);
      onBusinessUnitChange(foundBusinessUnit);
      
      // Auto-disable admin view when vendor/BU is manually selected
      if (isAdminView && canToggleAdminView) {
       disableAdminView();
      }
      return;
    }

    if (vendorsList?.length > 0) {
      const newVendor = vendorsList.find(vendor => vendor.name === vendorName);
      onVendorChange(newVendor ? newVendor : null);
      
      // Auto-select "All" business units when vendor changes
      onBusinessUnitChange(null);
      
      // Auto-disable admin view when vendor is manually selected
      if (isAdminView && canToggleAdminView && newVendor) {
        disableAdminView();
      }
    }
  }, [
    businessUnitList, 
    vendorsList, 
    onVendorChange, 
    onBusinessUnitChange, 
    isAdminView, 
    canToggleAdminView,
    disableAdminView
  ]);

  /**
   * Handles business unit selection with automatic admin view disable
   * Auto-disable admin view when business unit is manually selected
   * Handles null value to represent "All" business units selection
   */
  const handleBusinessUnitSelection = useCallback((businessUnitId: string | null) => {
    // If null is passed, it means "All" business units are selected
    if (businessUnitId === null) {
      onBusinessUnitChange(null);
      // Keep the current vendor selection when "All" is selected
      return;
    }

    const newBusinessUnit = businessUnitList?.find(bu => bu.id === parseInt(businessUnitId || '', 10));
    onBusinessUnitChange(newBusinessUnit ? newBusinessUnit : null);
    
    const vendor = vendorsList.find(vendor => vendor.id === newBusinessUnit?.vendorId) || null;
    onVendorChange(vendor);
    
    // Auto-disable admin view when vendor/BU is manually selected
    if (isAdminView && canToggleAdminView && newBusinessUnit) {
      disableAdminView();
    }
  }, [
    businessUnitList, 
    vendorsList, 
    onVendorChange, 
    onBusinessUnitChange, 
    isAdminView, 
    canToggleAdminView
  ]);

  return {
    isAdminView,
    canToggleAdminView,
    handleAdminViewToggle,
    handleVendorSelection,
    handleBusinessUnitSelection
  };
};